import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  ChangeDetectorRef,
  ContentChildren,
  QueryList,
  AfterContentInit,
  OnDestroy,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AppRadioComponent } from './app-radio.component';

export interface RadioOption {
  value: any;
  label: string;
  description?: string;
  disabled?: boolean;
}

@Component({
  selector: 'app-radio-group',
  templateUrl: './app-radio-group.component.html',
  styleUrls: ['./app-radio-group.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppRadioGroupComponent),
      multi: true,
    },
  ],
})
export class AppRadioGroupComponent implements ControlValueAccessor, AfterContentInit, OnDestroy {
  @Input() labelText: string = '';
  @Input() label: string = '';
  @Input() mandatory: boolean = false;
  @Input() required: boolean = false;
  @Input() optional: boolean = false;
  @Input() tooltip: string = '';
  @Input() labelIcon: string = '';
  @Input() labelSize: 'sm' | 'md' | 'lg' = 'md';

  get resolvedLabel(): string {
    return this.labelText || this.label || '';
  }

  get isRequired(): boolean {
    return this.mandatory || this.required;
  }

  @Input() options: RadioOption[] = [];
  @Input() row: boolean = true;
  @Input() gap: string = '12px';
  @Input() name: string = '';
  @Input() disabled: boolean = false;
  @Input() errorMsg: string | null = null;
  @Input() helperText: string = '';
  @Input() customClass: string = '';

  @Output() change = new EventEmitter<any>();

  @ContentChildren(forwardRef(() => AppRadioComponent))
  projectedRadios!: QueryList<AppRadioComponent>;

  value: any = null;
  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};
  private radioSubscriptions: Subscription[] = [];

  constructor(private cdr: ChangeDetectorRef) {}

  ngAfterContentInit(): void {
    this.updateProjectedRadios();
    this.projectedRadios.changes.subscribe(() => {
      this.updateProjectedRadios();
    });
  }

  ngOnDestroy(): void {
    this.radioSubscriptions.forEach((sub) => sub.unsubscribe());
  }

  private updateProjectedRadios(): void {
    this.radioSubscriptions.forEach((sub) => sub.unsubscribe());
    this.radioSubscriptions = [];

    if (this.projectedRadios) {
      this.projectedRadios.forEach((radio) => {
        radio.writeValue(this.value);
        if (this.disabled) {
          radio.setDisabledState(true);
        }
        const sub = radio.change.subscribe((val) => {
          this.onSelect(val);
        });
        this.radioSubscriptions.push(sub);
      });
    }
  }

  writeValue(val: any): void {
    this.value = val;
    if (this.projectedRadios) {
      this.projectedRadios.forEach((radio) => radio.writeValue(val));
    }
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (this.projectedRadios) {
      this.projectedRadios.forEach((radio) => radio.setDisabledState(isDisabled));
    }
    this.cdr.markForCheck();
  }

  onSelect(val: any): void {
    if (this.disabled) return;
    this.value = val;
    this.onChange(val);
    this.onTouched();
    this.change.emit(val);
    if (this.projectedRadios) {
      this.projectedRadios.forEach((radio) => radio.writeValue(val));
    }
    this.cdr.markForCheck();
  }

  get hasError(): boolean {
    return !!this.errorMsg && this.errorMsg.trim().length > 0;
  }
}
